import React from 'react';
import type { Entity } from '../../types/api';
import { formatColumnHeader, formatCellValue, isMissing, hasValuesInType } from './helpers';

interface ComponentTableProps {
  entities: Entity[];
  configKeys: string[];
  metadataKeys: string[];
}

const ComponentTable: React.FC<ComponentTableProps> = ({ entities, configKeys, metadataKeys }) => {
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[var(--color-surface)] border-b-2 border-[var(--color-border)]">
              <th className="text-left px-3 py-2 text-engramiq-green font-semibold border-r border-[var(--color-border)] sticky left-0 bg-[var(--color-bg-primary)] z-10">
                Component Name
              </th>
              <th className="text-left px-3 py-2 text-engramiq-green font-semibold border-r border-[var(--color-border)] min-w-[100px]">
                Alias
              </th>
              {configKeys.map(key => (
                <th
                  key={`config-${key}`}
                  className="text-left px-3 py-2 text-cyan-400 font-semibold border-r border-[var(--color-border)] min-w-[120px]"
                >
                  {formatColumnHeader(key)}
                </th>
              ))}
              {metadataKeys.map(key => (
                <th
                  key={`meta-${key}`}
                  className="text-left px-3 py-2 text-engramiq-green font-semibold border-r border-[var(--color-border)] min-w-[120px]"
                >
                  {formatColumnHeader(key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entities.slice(0, 100).map((entity, idx) => {
              const name = entity.name || entity.entity_name || 'Unknown';
              const entityId = entity.id || entity.entity_id;
              const aliases = entity.aliases || [];
              const configuration = entity.configuration || {};
              const metadata = entity.entity_metadata || entity.metadata || entity.properties || {};

              return (
                <tr
                  key={entityId}
                  className={`border-b border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] transition-colors ${
                    idx % 2 === 0 ? 'bg-[var(--color-surface)]' : 'bg-transparent'
                  }`}
                >
                  <td className="px-3 py-2 eg-text-primary font-mono text-xs border-r border-[var(--color-border)] sticky left-0 bg-[var(--color-bg-primary)]">
                    {name}
                  </td>
                  <td className="px-3 py-2 eg-text-primary text-xs border-r border-[var(--color-border)]">
                    {aliases.length > 0 ? aliases.join(', ') : '—'}
                  </td>
                  {configKeys.map(key => {
                    const value = configuration[key];
                    const missing = isMissing(value);

                    return (
                      <td key={`config-${key}`} className="px-3 py-2 border-r border-[var(--color-border)]">
                        <span className={`text-xs ${missing ? 'eg-text-secondary/50 italic' : 'eg-text-primary'}`}>
                          {formatCellValue(value) || '—'}
                        </span>
                      </td>
                    );
                  })}
                  {metadataKeys.map(key => {
                    const value = metadata[key];
                    const missing = isMissing(value);
                    const shouldHighlight = missing && hasValuesInType(entities, key);

                    return (
                      <td
                        key={`meta-${key}`}
                        className={`px-3 py-2 border-r border-[var(--color-border)] ${
                          shouldHighlight ? 'border-2 border-accent-red/60' : ''
                        }`}
                      >
                        <span className={`text-xs ${missing ? 'eg-text-secondary/50 italic' : 'eg-text-primary'}`}>
                          {formatCellValue(value) || '—'}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {entities.length > 100 && (
        <div className="mt-3 eg-text-secondary text-sm italic">
          Showing first 100 of {entities.length} nodes. Use search to filter.
        </div>
      )}
    </>
  );
};

export default ComponentTable;
